"use client";

import "twin.macro";

import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Client } from "@/api/clients/types";
import { useCreateUser } from "@/api/users/mutations";
import Error from "@/components/error";
import Field from "@/components/field";
import Input from "@/components/inputs";
import Label from "@/components/label";

interface UpdateClientModalFormProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  client: Client;
}

type UpdateClientValues = {
  lastname: string;
  firstname: string;
  email: string;
  client_id: string;
};

export const CreateUserModalForm = ({
  open,
  setOpen,
  client,
}: UpdateClientModalFormProps) => {
  const { t } = useTranslation();
  const createuser = useCreateUser(client._id);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitSuccessful },
  } = useForm<UpdateClientValues>({
    defaultValues: {
      lastname: "",
      firstname: "",
      email: "",
      client_id: client._id,
    },
  });

  const onSubmit: SubmitHandler<UpdateClientValues> = async (data) => {
    try {
      await createuser.mutateAsync(data);
      setOpen(false); // Fermer le modal après succès
      reset(); // Réinitialiser le formulaire
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    setOpen(false);
    reset();
  }, [isSubmitSuccessful]);

  return (
    <Dialog open={open} onClose={setOpen} tw="relative z-10">
      <DialogBackdrop
        transition
        tw="fixed inset-0 bg-gray-500/75 transition-opacity duration-500 ease-in-out data-[closed]:opacity-0"
      />

      <div tw="fixed inset-0 overflow-hidden">
        <div tw="absolute inset-0 overflow-hidden">
          <div tw="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
            <DialogPanel
              transition
              tw="pointer-events-auto relative w-screen max-w-md transform transition duration-500 ease-in-out data-[closed]:translate-x-full sm:duration-700"
            >
              <div tw="flex h-full flex-col bg-white py-6 shadow-xl overflow-auto">
                <div tw="px-4 sm:px-6">
                  <div tw="flex items-start justify-between">
                    <DialogTitle tw="mt-6 text-lg font-semibold text-gray-900">
                      {t("form.addUser.title")}
                    </DialogTitle>
                    <div tw="ml-3 flex h-7 items-center">
                      <button
                        type="button"
                        onClick={() => setOpen(false)}
                        tw="relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600"
                      >
                        <span tw="absolute -inset-2.5" />
                        <span tw="sr-only">{t("form.close")}</span>
                        <XMarkIcon aria-hidden="true" tw="w-6 h-6" />
                      </button>
                    </div>
                  </div>
                </div>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div tw="flex flex-col mt-8 gap-6 px-4 sm:px-6">
                    {/* 
                    //todo : add role to user
                    <Field tw="flex-1">
                      <Label>{t("users.role")}</Label>
                      <Select {...register("user.role", { required: true })}>
                        <RoleOptions />
                      </Select>
                      {errors.user?.role?.type === "required" && (
                        <Error>{t("form.mandatory")}</Error>
                      )}
                    </Field> */}
                    {/* <Field tw="flex-1">
                      <Label>{t("users.registration")}</Label>
                      <Input
                        {...register("user.registration")}
                      />
                    </Field> */}
                    <Field tw="flex-1">
                      <Label>{t("users.lastname")}</Label>
                      <Input
                        {...register("lastname", { required: true })}
                        invalid={Boolean(errors.lastname)}
                      />
                      {errors.lastname?.type === "required" && (
                        <Error>{t("form.mandatory")}</Error>
                      )}
                    </Field>
                    <Field tw="flex-1">
                      <Label>{t("users.firstname")}</Label>
                      <Input
                        {...register("firstname", { required: true })}
                        invalid={Boolean(errors.firstname)}
                      />
                      {errors.firstname?.type === "required" && (
                        <Error>{t("form.mandatory")}</Error>
                      )}
                    </Field>
                    <Field tw="flex-1">
                      <Label>{t("users.email")}</Label>
                      <Input
                        {...register("email", { required: true })}
                        invalid={Boolean(errors.email)}
                      />
                      {errors.email?.type === "required" && (
                        <Error>{t("form.mandatory")}</Error>
                      )}
                    </Field>
                  </div>
                  <div tw="flex mt-8 shrink-0 justify-end px-4 py-4">
                    <button
                      type="button"
                      onClick={() => setOpen(false)}
                      tw="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:ring-gray-400"
                    >
                      {t("global.cancel")}
                    </button>
                    <button
                      type="submit"
                      tw="ml-4 inline-flex justify-center rounded-md bg-primary-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-800"
                    >
                      {t("global.save")}
                    </button>
                  </div>
                </form>
              </div>
            </DialogPanel>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
